import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLogin, useGoogleLogin } from '@/hooks/useApi';
import { useAuthStore } from '@/stores/authStore';
import GoogleSignInButton from '@/components/auth/GoogleSignInButton';
import toast from 'react-hot-toast';
import type { AxiosError } from 'axios';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'nl', label: 'NL' },
  { code: 'es', label: 'ES' },
];

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const login = useLogin();
  const googleLogin = useGoogleLogin();
  const setAuth = useAuthStore((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [, setLanguageUpdate] = useState(0);

  useEffect(() => {
    const handleLanguageChanged = () => {
      setLanguageUpdate((prev) => prev + 1);
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('ledgora-lang', lang);
  };

  const handleGoogleCredential = async (idToken: string) => {
    setError('');
    try {
      const result = await googleLogin.mutateAsync({ idToken });
      setAuth(result.token, result.refreshToken, result.user);
      if (result.organization?.id) {
        useAuthStore.getState().setOrganizationId(result.organization.id);
      }
      toast.success(t('auth.loginSuccess'));
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; error?: string }>;
      const status = axiosErr?.response?.status;
      const errorCode = axiosErr?.response?.data?.error;
      if (status === 403 && errorCode === 'google_email_not_verified') {
        setError(t('auth.googleEmailNotVerified'));
      } else {
        setError(axiosErr?.response?.data?.message || t('auth.googleSignInFailed'));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const result = await login.mutateAsync({ email, password });
      setAuth(result.token, result.refreshToken, result.user);
      if (result.organization?.id) {
        useAuthStore.getState().setOrganizationId(result.organization.id);
      }
      toast.success(t('auth.loginSuccess'));
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string; error?: string; email?: string }>;
      const status = axiosErr?.response?.status;
      const serverMsg = axiosErr?.response?.data?.message;

      const errorCode = axiosErr?.response?.data?.error;
      const resEmail = axiosErr?.response?.data?.email;

      if (status === 403 && errorCode === 'email_not_verified') {
        navigate('/confirm-account?email=' + encodeURIComponent(resEmail || email));
        return;
      }

      if (status === 401) {
        setError(serverMsg || t('auth.invalidCredentials'));
      } else if (status === 429) {
        setError(t('auth.tooManyAttempts'));
      } else {
        setError(serverMsg || t('auth.loginFailed'));
      }
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="lg:hidden flex flex-col items-center gap-2 mb-6 w-full justify-center">
          <img src="/logo.svg" alt="Ledgora" className="w-10 h-10" />
          <span className="text-xl font-bold text-gray-900">{import.meta.env.VITE_APP_NAME}</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.login')}</h2>
        <p className="mt-2 text-sm text-gray-600">
          {t('auth.noAccount')}{' '}
          <Link to="/register" className="text-primary-600 hover:text-primary-500 font-medium">
            {t('auth.register')}
          </Link>
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
          <svg className="w-5 h-5 text-red-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Google Sign-In */}
      <div className="mb-5 relative">
        <GoogleSignInButton
          onCredential={handleGoogleCredential}
          text="signin_with"
          disabled={googleLogin.isPending || login.isPending}
        />
        {googleLogin.isPending && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-white/80 text-sm font-medium text-gray-600">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            {t('auth.signingIn')}
          </div>
        )}
      </div>

      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-3 text-gray-400 uppercase tracking-wider">
            {t('auth.orContinueWithEmail')}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="label">{t('auth.email')}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            className="input"
            required
            autoFocus
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="label">{t('auth.password')}</label>
            <Link to="/forgot-password" className="text-xs text-primary-600 hover:text-primary-500 font-medium">
              {t('auth.forgotPassword')}
            </Link>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              className="input pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={login.isPending || googleLogin.isPending}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {login.isPending && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
          )}
          {login.isPending ? t('auth.signingIn') : t('auth.login')}
        </button>
      </form>

      {/* Language switcher — discreet, at the bottom */}
      <div className="flex justify-center mt-8 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-1">
          {LANGUAGES.map((lang, i) => (
            <span key={lang.code} className="flex items-center">
              <button
                onClick={() => changeLanguage(lang.code)}
                className={`text-xs transition-colors ${
                  i18n.language === lang.code
                    ? 'text-primary-600 font-semibold'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {lang.label}
              </button>
              {i < LANGUAGES.length - 1 && (
                <span className="text-gray-300 mx-1 text-[10px]">·</span>
              )}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
